// Brush preview in the Paint Tool panel, painted by the production GPU engine
// (integration/brushPreview.js) with the current settings and document paper.
// Regenerated 300 ms after the last settings change; cached previews appear at once.

import React, { useEffect, useRef, useState } from 'react';
import { getReadyPaintDevice } from './physicalPaintFlag.js';
import { requestBrushPreview, releaseBrushPreviewRequester, previewKey, previewScale, PREVIEW_CELLS } from './brushPreview.js';

const DEBOUNCE_MS = 300;
const labelStyle = { fontSize: 10, fontWeight: 950, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' };

export default function BrushPreviewSwatch({ settings }) {
  const canvasRef = useRef(null);
  // Identifies this preview to the shared renderer (supersession is per preview).
  const requesterRef = useRef({});
  const [surface, setSurface] = useState({});
  const [status, setStatus] = useState('');
  const key = previewKey(settings, surface);

  // The document paper, from the Paper & Surface system.
  useEffect(() => {
    const onSurface = (event) => { if (event && event.detail) setSurface({ ...event.detail }); };
    window.addEventListener('vx-paint-surface', onSurface);
    window.addEventListener('vx-paint-surface-set', onSurface);
    window.dispatchEvent(new CustomEvent('vx-paint-surface-query'));
    return () => {
      window.removeEventListener('vx-paint-surface', onSurface);
      window.removeEventListener('vx-paint-surface-set', onSurface);
    };
  }, []);

  useEffect(() => {
    const requester = requesterRef.current;
    return () => releaseBrushPreviewRequester(requester);
  }, []);

  useEffect(() => {
    const gpu = getReadyPaintDevice();
    if (!gpu) return undefined;
    let cancelled = false;
    setStatus('Painting preview…');
    const timer = setTimeout(() => {
      requestBrushPreview(gpu, settings, surface, requesterRef.current).then((bitmap) => {
        if (cancelled || !bitmap || !canvasRef.current) return;
        const ctx = canvasRef.current.getContext('2d');
        ctx.drawImage(bitmap, 0, 0, canvasRef.current.width, canvasRef.current.height);
        setStatus('');
      }).catch(() => { if (!cancelled) setStatus('Preview unavailable'); });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // The key captures every setting the preview depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const { scale } = previewScale(settings);
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <span style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between' }}>
        <span>Brush preview</span>
        <span style={{ color: 'rgba(255,255,255,0.5)' }}>{scale < 1 ? `shown at ${Math.round(scale * 100)}%` : 'actual size'}</span>
      </span>
      <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.24)' }}>
        <canvas
          ref={canvasRef}
          width={PREVIEW_CELLS.width * 2}
          height={PREVIEW_CELLS.height * 2}
          aria-label="Brush preview painted by the paint engine"
          style={{ display: 'block', width: '100%', height: 'auto' }}
        />
        {status ? (
          <span style={{ position: 'absolute', right: 6, bottom: 4, fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.7)', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>{status}</span>
        ) : null}
      </div>
    </div>
  );
}
